import React, { useState, useEffect, useMemo } from 'react';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import toast from 'react-hot-toast';
import {
  Tag,
  Plus,
  Search,
  RefreshCw,
  Eye,
  X,
  Percent,
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [calculating, setCalculating] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // Form state for creating new coupon
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountPercentage: '',
    maxDiscount: '',
    reference: '',
    commission: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}api/coupon/get-all`, {
        withCredentials: true
      });
      setCoupons(response.data.data || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch promotions');
      setLoading(false);
      console.error('Error fetching coupons:', err);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${baseUrl}api/coupon/create`, newCoupon, {
        withCredentials: true
      });
      setCoupons([response.data.data, ...coupons]);
      setShowCreateModal(false);
      setNewCoupon({
        code: '',
        discountPercentage: '',
        maxDiscount: '',
        reference: '',
        commission: '',
        isActive: true
      });
      toast.success('Promotion coupon created successfully');
    } catch (err) {
      console.error('Error creating coupon:', err);
      toast.error('Failed to create coupon. Please check all fields.');
    }
  };

  const handleCalculateStats = async (couponId) => {
    try {
      setCalculating(couponId);
      const response = await axios.put(`${baseUrl}api/coupon/calculate/${couponId}`, {}, {
        withCredentials: true
      });

      setCoupons(coupons.map(coupon =>
        coupon._id === couponId
          ? { ...coupon, usage: response.data.data }
          : coupon
      ));
      if (selectedCoupon && selectedCoupon._id === couponId) {
        setSelectedCoupon(prev => ({ ...prev, usage: response.data.data }));
      }
      toast.success('Performance statistics updated');
      setCalculating(null);
    } catch (err) {
      console.error('Error calculating coupon stats:', err);
      toast.error('Failed to update stats');
      setCalculating(null);
    }
  };

  const handleViewCoupon = (coupon) => {
    setSelectedCoupon(coupon);
    setShowCouponModal(true);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(`Copied "${code}" to clipboard`);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  // Metrics summary
  const metrics = useMemo(() => {
    const total = coupons.length;
    const active = coupons.filter(c => c.isActive).length;
    const totalRedemptions = coupons.reduce((sum, c) => sum + (c.usage?.totalUses || 0), 0);
    const totalRevenueDriven = coupons.reduce((sum, c) => sum + (c.usage?.totalSales || 0), 0);

    return { total, active, totalRedemptions, totalRevenueDriven };
  }, [coupons]);

  // Filtered dataset
  const filteredCoupons = useMemo(() => {
    return coupons.filter(coupon => {
      // Status filter
      if (statusFilter === 'active' && !coupon.isActive) return false;
      if (statusFilter === 'inactive' && coupon.isActive) return false;

      // Search query
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        coupon.code?.toLowerCase().includes(q) ||
        coupon.reference?.toLowerCase().includes(q)
      );
    });
  }, [coupons, statusFilter, searchQuery]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Promotions & Discounts</h1>
            <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200/80 font-mono px-2 py-0.5 rounded-full font-semibold">
              {metrics.active} Active
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Manage promotional voucher codes, percentage discounts, customer usage, and campaign commissions.
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-stone-900 text-white text-xs font-semibold hover:bg-stone-800 transition-colors shadow-xs shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Promotion
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Total Coupons</div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">{metrics.total}</div>
          <div className="text-[11px] text-stone-500 mt-1">Configured promo codes</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Active Campaigns</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{metrics.active}</div>
          <div className="text-[11px] text-stone-500 mt-1">Ready for checkout redemption</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Total Redemptions</div>
          <div className="text-2xl font-bold text-amber-600 mt-1 font-mono">{metrics.totalRedemptions}</div>
          <div className="text-[11px] text-stone-500 mt-1">Orders with discounts applied</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Revenue Influenced</div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">{formatCurrency(metrics.totalRevenueDriven)}</div>
          <div className="text-[11px] text-stone-500 mt-1">Gross sales from promo orders</div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        {/* Filter and Search Bar */}
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1">
            {[
              { id: 'all', label: 'All Promotions', count: coupons.length },
              { id: 'active', label: 'Active', count: metrics.active },
              { id: 'inactive', label: 'Inactive', count: coupons.length - metrics.active }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  statusFilter === tab.id
                    ? 'bg-stone-900 text-white font-semibold shadow-xs'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[10px] ${
                  statusFilter === tab.id ? 'bg-stone-800 text-stone-200' : 'bg-stone-100 text-stone-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search code or reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-lg py-1.5 pl-8 pr-3 text-xs text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:bg-white transition-all"
            />
            <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* Loading / Error States */}
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-amber-600 mb-3"></div>
            <p className="text-xs text-stone-500 font-medium">Loading promotional campaigns...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <p className="text-xs text-rose-600 font-medium">{error}</p>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="p-12 text-center">
            <Tag className="w-8 h-8 text-stone-300 mx-auto mb-2" />
            <p className="text-xs text-stone-500 font-medium">No coupons found matching your criteria.</p>
            <p className="text-[11px] text-stone-400 mt-1">Create a new coupon code or adjust your search filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 text-[11px] font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
                  <th className="py-3 px-4">Coupon Code</th>
                  <th className="py-3 px-4">Discount Rate</th>
                  <th className="py-3 px-4">Max Cap / Commission</th>
                  <th className="py-3 px-4">Usage & Volume</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 text-xs">
                {filteredCoupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-stone-50/70 transition-colors">
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-amber-900 bg-amber-50/80 border border-amber-200/80 px-2 py-0.5 rounded text-xs">
                          {coupon.code}
                        </span>
                        <button
                          onClick={() => handleCopyCode(coupon.code)}
                          className="p-1 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded transition-colors"
                          title="Copy Code"
                        >
                          {copiedCode === coupon.code ? (
                            <Check className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                      {coupon.reference && (
                        <div className="text-[11px] text-stone-400 mt-0.5 truncate max-w-xs">
                          Ref: {coupon.reference}
                        </div>
                      )}
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap font-medium text-stone-900">
                      <span className="inline-flex items-center gap-1 font-bold text-sm">
                        {coupon.discountPercentage}%
                      </span>
                      <span className="text-stone-400 text-[11px] ml-1">OFF</span>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="text-stone-900 font-mono font-medium">
                        Cap: {coupon.maxDiscount ? formatCurrency(coupon.maxDiscount) : 'No limit'}
                      </div>
                      {coupon.commission ? (
                        <div className="text-[11px] text-stone-500 mt-0.5">
                          Commission: {coupon.commission}%
                        </div>
                      ) : (
                        <div className="text-[11px] text-stone-400 mt-0.5">No commission</div>
                      )}
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="font-semibold text-stone-900 font-mono">
                        {coupon.usage?.totalUses || 0} orders
                      </div>
                      <div className="text-[11px] text-stone-500 font-mono mt-0.5">
                        {formatCurrency(coupon.usage?.totalSales || 0)} gross
                      </div>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${
                        coupon.isActive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-stone-100 text-stone-600 border-stone-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${coupon.isActive ? 'bg-emerald-500' : 'bg-stone-400'}`} />
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleCalculateStats(coupon._id)}
                          disabled={calculating === coupon._id}
                          className="p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Refresh Stats"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${calculating === coupon._id ? 'animate-spin text-amber-600' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleViewCoupon(coupon)}
                          className="p-1.5 text-stone-500 hover:text-amber-700 hover:bg-stone-100 rounded-lg transition-colors"
                          title="View Campaign Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Coupon Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center px-6 py-4 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
                  <Tag className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-stone-900">Create Promotion Coupon</h2>
                  <p className="text-[11px] text-stone-500">Configure voucher specifications and discount rules</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateCoupon} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-stone-700 mb-1">Coupon Code *</label>
                <input
                  type="text"
                  required
                  className="w-full uppercase font-mono font-bold rounded-lg border border-stone-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. FLASH20"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Discount % *</label>
                  <div className="relative">
                    <input
                      type="number"
                      required
                      min="1"
                      max="100"
                      className="w-full rounded-lg border border-stone-200 px-3 py-2 pr-7 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                      value={newCoupon.discountPercentage}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discountPercentage: e.target.value })}
                      placeholder="20"
                    />
                    <Percent className="w-3.5 h-3.5 text-stone-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Max Cap (£) *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={newCoupon.maxDiscount}
                    onChange={(e) => setNewCoupon({ ...newCoupon, maxDiscount: e.target.value })}
                    placeholder="50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Commission %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={newCoupon.commission}
                    onChange={(e) => setNewCoupon({ ...newCoupon, commission: e.target.value })}
                    placeholder="e.g. 10"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-stone-700 mb-1">Campaign Reference</label>
                  <input
                    type="text"
                    required
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    value={newCoupon.reference}
                    onChange={(e) => setNewCoupon({ ...newCoupon, reference: e.target.value })}
                    placeholder="e.g. Spring Sale"
                  />
                </div>
              </div>

              <div className="pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newCoupon.isActive}
                    onChange={(e) => setNewCoupon({ ...newCoupon, isActive: e.target.checked })}
                    className="w-4 h-4 rounded text-amber-700 border-stone-300 focus:ring-amber-500"
                  />
                  <span className="text-xs font-medium text-stone-800">
                    Activate immediately for customer checkout
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-stone-900 text-white font-semibold hover:bg-stone-800 transition-colors shadow-xs"
                >
                  Create Promotion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Coupon Details Modal */}
      {showCouponModal && selectedCoupon && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center px-6 py-4 border-b border-stone-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg text-sm">
                  {selectedCoupon.code}
                </span>
                <div>
                  <h2 className="text-sm font-bold text-stone-900">{selectedCoupon.reference || 'Promotion Details'}</h2>
                  <p className="text-[11px] text-stone-500">Full audit breakdown and checkout redemption log</p>
                </div>
              </div>
              <button
                onClick={() => setShowCouponModal(false)}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-6 text-xs">
              {/* Economics Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-stone-50 border border-stone-200/80">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Discount Offered</div>
                  <div className="text-xl font-bold text-stone-900 font-mono mt-1">{selectedCoupon.discountPercentage}% OFF</div>
                  <div className="text-[10px] text-stone-400 mt-0.5">Cap: {formatCurrency(selectedCoupon.maxDiscount)}</div>
                </div>

                <div className="p-3.5 rounded-xl bg-amber-50/60 border border-amber-200/80">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">Total Redemptions</div>
                  <div className="text-xl font-bold text-amber-950 font-mono mt-1">{selectedCoupon.usage?.totalUses || 0}</div>
                  <div className="text-[10px] text-amber-700 mt-0.5">Checkout orders</div>
                </div>

                <div className="p-3.5 rounded-xl bg-emerald-50/60 border border-emerald-200/80">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800">Total Sales Driven</div>
                  <div className="text-xl font-bold text-emerald-950 font-mono mt-1">{formatCurrency(selectedCoupon.usage?.totalSales || 0)}</div>
                  <div className="text-[10px] text-emerald-700 mt-0.5">Order gross revenue</div>
                </div>
              </div>

              {/* Campaign Specifications */}
              <div className="rounded-xl border border-stone-200 p-4 space-y-2 bg-stone-50/40">
                <h3 className="font-semibold text-stone-900 text-xs mb-2">Campaign Specifications</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-stone-500">Status: </span>
                    <span className={`inline-flex items-center gap-1 font-semibold ${selectedCoupon.isActive ? 'text-emerald-700' : 'text-stone-600'}`}>
                      {selectedCoupon.isActive ? 'Active & Redeemable' : 'Inactive / Paused'}
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-500">Affiliate Commission: </span>
                    <span className="font-semibold text-stone-900">{selectedCoupon.commission || 0}%</span>
                  </div>
                  <div>
                    <span className="text-stone-500">Created: </span>
                    <span className="font-mono text-stone-800">
                      {selectedCoupon.createdAt ? new Date(selectedCoupon.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-500">Last Synced: </span>
                    <span className="font-mono text-stone-800">
                      {selectedCoupon.updatedAt ? new Date(selectedCoupon.updatedAt).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order History */}
              <div>
                <h3 className="font-semibold text-stone-900 text-xs mb-3">Redemption Order History</h3>
                {(!selectedCoupon.usage?.orders || selectedCoupon.usage.orders.length === 0) ? (
                  <div className="p-6 text-center rounded-xl border border-dashed border-stone-200 bg-stone-50/50 text-stone-400">
                    No orders have applied this voucher code yet.
                  </div>
                ) : (
                  <div className="border border-stone-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-left">
                      <thead className="bg-stone-50 text-[10px] uppercase font-semibold text-stone-500 border-b border-stone-100">
                        <tr>
                          <th className="py-2 px-3">Order Number</th>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100 text-xs">
                        {selectedCoupon.usage.orders
                          .filter(ow => ow.orderId)
                          .map((ow, idx) => (
                            <tr key={idx} className="hover:bg-stone-50/60">
                              <td className="py-2 px-3 font-mono font-medium text-stone-900">
                                {ow.orderId?.orderNumber || 'MO-ORD'}
                              </td>
                              <td className="py-2 px-3">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-stone-100 text-stone-700 capitalize">
                                  {ow.orderId?.status || 'placed'}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right font-mono font-semibold text-stone-900">
                                {formatCurrency(ow.orderId?.pricing?.totalAmount || 0)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-stone-100">
                <button
                  onClick={() => handleCalculateStats(selectedCoupon._id)}
                  disabled={calculating === selectedCoupon._id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${calculating === selectedCoupon._id ? 'animate-spin text-amber-600' : ''}`} />
                  Recalculate Stats
                </button>

                <button
                  onClick={() => setShowCouponModal(false)}
                  className="px-4 py-2 rounded-lg bg-stone-900 text-white font-semibold hover:bg-stone-800 transition-colors shadow-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;